import React from 'react';
import {Column} from 'src/model/table';
import getCountryFlag from 'src/utils/countries-flags';
import {Direction, IPDirection} from 'src/utils/enums';
import {arrayToMap} from 'src/utils/map-and-array';
import {Severity} from '../virtualized-table/sevirity/sevirity';
import {AppDto} from '../../../../dtos/app.dto';
import {DisplayNameCell} from '../../inventory/inventory-utils';
import {SystemConfiguration} from '@src/utils/system-configuration';

export interface AllReportsFieldsConfig {
    index: string;
    application?: string;
    applicationData?: AppDto;
    src?: string;
    dst?: string;
    direction?: IPDirection | Direction;
    bytes?: number;
    dport?: string;
    proto?: string,
    rule?: string;
    sessions?: number;
    category?: string;
    srcLoc: string,
    srcLocName: string,
    domain:string,
    dstLoc: string,
    dstLocName: string,
    count?: number;
    maxBytes?: number;
    maxCount?: number;
    maxSessions?: number;
    url?: string;
    bytes_received?: number,
    bytes_sent?: number,
    action?: string,
    subtype?: string,
    threatid?: string,
    severity?: string,
    tid?: string,
    country: string,
    transport: string,
    percentOfTotal?: number,
    aiDefense?: boolean,
}

const countDisplayName = (row: any, countField: keyof AllReportsFieldsConfig, maxCountField: keyof AllReportsFieldsConfig) => <div className="top-10-count-column">
    <div className="bar-chart-count">{row[countField]}</div>
    <div className="bar-chart-wrapper">
        <div style={{ width: `${Math.max(row[countField] * 100 / (row[maxCountField] || row[countField]), 0.8)}%` }} className="bar-chart" />
    </div>
</div>;


export const countryWithFlag = (row: AllReportsFieldsConfig, countryFieldName: keyof AllReportsFieldsConfig) => {
    const codesByNames =  SystemConfiguration.getInstance().getCountriesMapByOfficialNameIncludeHidden;

    const countryDetails = codesByNames[row[countryFieldName] as string];

    const FlagSvg = getCountryFlag(countryDetails?.countryCode ?? '');
    return <div className="country-with-flag">
        {FlagSvg && <FlagSvg />}
        <div className="country-name">{countryDetails?.countryName ?? row[countryFieldName] as string}</div>
    </div>;
};

export const allTop10CommonColumns: Column<AllReportsFieldsConfig>[] = [{
    accessor: 'index',
    header: '#',
    width: '3%',
}, {
    accessor: 'application',
    header: 'Application',
    width: '25%',
}, {
    accessor: 'rule',
    header: 'Rule',
    width: '24%',
}, {
    accessor: 'proto',
    header: 'Protocol',
    width: '7%',
}, {
    accessor: 'src',
    header: 'Source Address',
    width: '22%',
}, {
    accessor: 'dst',
    header: 'Destination Address',
    width: '22%',
}, {
    accessor: 'direction',
    header: 'Direction',
    width: '22%',
}, {
    accessor: 'dport',
    header: 'Destination Port',
    width: '10%',
}, {
    accessor: 'sessions',
    header: 'Sessions',
    width: '20%',
    getDisplayElement: (column: any, row: AllReportsFieldsConfig) => countDisplayName(row, 'sessions', 'maxSessions')
}, {
    accessor: 'category',
    header: 'Category',
    width: '25%',
}, {
    accessor: 'domain',
    header: 'Domain',
    width: '25%',
    getDisplayElement: (column: any, row: AllReportsFieldsConfig) => <DisplayNameCell displayName={row.domain ?? ''}/>,
}, {
    accessor: 'dstLocName',
    header: 'Destination Country',
    width: '16%',
    getDisplayElement: (column: any, row: AllReportsFieldsConfig) => countryWithFlag(row, 'dstLocName')
}, {
    accessor: 'srcLocName',
    header: 'Source Country',
    width: '15%',
    getDisplayElement: (column: any, row: AllReportsFieldsConfig) => countryWithFlag(row, 'srcLocName')
}, {
    accessor: 'country',
    header: 'Country',
    width: '30%',
    getDisplayElement: (column: any, row: AllReportsFieldsConfig) => countryWithFlag(row, 'country')
}, {
    accessor: 'url',
    header: 'URL',
    width: '25%',
    getDisplayElement: (column: any, row: AllReportsFieldsConfig) => <DisplayNameCell displayName={row.url ?? ''} />,
}, {
    accessor: 'bytes',
    header: 'MB',
    width: '20%',
    getDisplayElement: (column: any, row: AllReportsFieldsConfig) => countDisplayName(row, 'bytes', 'maxBytes')
}, {
    accessor: 'count',
    header: 'Count',
    width: '20%',
    getDisplayElement: (column: any, row: AllReportsFieldsConfig) => countDisplayName(row, 'count', 'maxCount')
}, {
    accessor: 'action',
    header: 'Action',
    width: '25%',
}, {
    accessor: 'subtype',
    header: 'Type',
    width: '15%',
}, {
    accessor: 'threatid',
    header: 'Threat',
    width: '28%',
    getDisplayElement: (column: any, row: AllReportsFieldsConfig) => <DisplayNameCell
        displayName={row.threatid ?? ''}/>,
}, {
    accessor: 'transport',
    header: 'Service',
    width: '28%',
}, {
    accessor: 'severity',
    header: 'Severity',
    getDisplayElement: (column, row) => <Severity severityId={row?.severity} />,
    width: '15%',
}, {
    accessor: 'percentOfTotal',
    header: '% of Total',
    width: '15%'
}];

const topCommonColumnsMap = arrayToMap(allTop10CommonColumns, _ => _.accessor, _ => _);

type NameAndWidth = { name: (keyof AllReportsFieldsConfig), width?: string, header?: string }[];
export const getColumnsByNames = (nameAndWidth: NameAndWidth, withIndex = false): Column<Partial<AllReportsFieldsConfig>>[] => {
    return [...withIndex ? [getColumnsByName('index')] : [],
    ...nameAndWidth.map(({ name, width, header }) => ({ ...getColumnsByName(name), ...(width ? { width } : {}), ...(header ? { header } : {}) }))];
};

const getColumnsByName = (name: string): Column<any> => topCommonColumnsMap[name];